import qs from "qs";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { RegisteredDeviceQueryParams } from "src/api/iot/iot-types";
import { Order } from "src/api/public-types";
import { BaseButton, BaseInputWithSearch, BaseTable } from "src/components";
import { usePartnerAuthority } from "src/hooks/usePartnerAuthority";
import useNavigate from "src/hooks/usePartnerNavigate";
import { PagePath } from "../product/details";
import { createIotConfigColumns } from "./columns/IotConfigColumns";
import { useIotManagementList } from "./hooks/useIotManagementList";
import SearchResetButton from "src/components/SearchResetButton";

const SEARCH_OPTIONS = [
  { label: "전체", value: "" },
  { label: "IoT ID", value: "id" },
  { label: "기기명", value: "containsInternalDisplayName" },
  { label: "기기명(노출)", value: "containsPrimaryDisplayName" },
  { label: "DisplayName", value: "containsLabelDisplayName" },
  { label: "Device ID", value: "containsPlatformDeviceId" },
  { label: "Name", value: "containsPlatformDeviceName" },
];

type SearchQueryParams = RegisteredDeviceQueryParams & {
  searchType?: string;
  searchKeyword?: string;
};

const IotConfigList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthority } = usePartnerAuthority();

  const queryParams = useMemo(() => {
    const query: SearchQueryParams = qs.parse(location.search, { allowDots: true, ignoreQueryPrefix: true });
    if (!query.page) {
      query.page = 0;
    }
    if (!query.size) {
      query.size = 20;
    }
    if (!query?.sort) {
      query.sort = {
        orders: [{ property: "id", direction: "DESC" }],
      };
    }
    if (!query?.searchType) {
      query.searchType = "";
    }

    return query;
  }, [location]);

  const [params, setParams] = useState<SearchQueryParams>(queryParams);

  // URL 의 searchType / searchKeyword 를 API 검색 파라미터로 변환
  const searchTypeAndKeyword = useMemo(() => {
    if (!queryParams.searchKeyword?.trim()) {
      return;
    }
    const searchData: SearchQueryParams = {};
    switch (queryParams.searchType) {
      case "id": // IoT ID
        searchData.id = queryParams.searchKeyword;
        break;
      case "containsInternalDisplayName": // 기기명
        searchData.containsInternalDisplayName = queryParams.searchKeyword;
        break;
      case "containsPrimaryDisplayName": // 기기명(노출)
        searchData.containsPrimaryDisplayName = queryParams.searchKeyword;
        break;
      case "containsLabelDisplayName": // DisplayName
        searchData.containsLabelDisplayName = queryParams.searchKeyword;
        break;
      case "containsPlatformDeviceId": // Device ID
        searchData.containsPlatformDeviceId = queryParams.searchKeyword;
        break;
      case "containsPlatformDeviceName": // Name
        searchData.containsPlatformDeviceName = queryParams.searchKeyword;
        break;
      default:
        searchData.search003 = queryParams.searchKeyword; // 전체
        break;
    }
    return searchData;
  }, [queryParams.searchKeyword, queryParams.searchType]);

  const disabledSortHeaders = useMemo(() => {
    return createIotConfigColumns(location.pathname)
      .filter((column) => column.Header && column.Header !== "id")
      .map((column) => column.accessor)
      .filter((accessor) => accessor.length > 0);
  }, [location.pathname]);

  useEffect(() => {
    if (!location.search) {
      setParams(queryParams);
    }
  }, [queryParams, location]);

  const listQueryParams = useMemo(() => ({ ...queryParams, ...searchTypeAndKeyword }), [queryParams, searchTypeAndKeyword]);

  const { list: iotDeviceList, pageMeta } = useIotManagementList(listQueryParams);

  const navigateWithQueryParams = (passParams?: SearchQueryParams, includeSearch = false) => {
    // API 전용 검색 필드는 URL 에 남기지 않는다. (searchType / searchKeyword 에서 매번 파생)
    const {
      id,
      containsInternalDisplayName,
      containsPrimaryDisplayName,
      containsLabelDisplayName,
      containsPlatformDeviceId,
      containsPlatformDeviceName,
      search003,
      ...cleanParams
    } = queryParams;

    const baseParams = includeSearch
      ? { ...cleanParams, searchType: params.searchType ?? "", searchKeyword: params.searchKeyword ?? "" }
      : cleanParams;

    const newQueryParams = { ...baseParams, ...(passParams || {}) };
    navigate(location.pathname + "?" + decodeURIComponent(qs.stringify(newQueryParams, { allowDots: true })));
  };

const iotConfigColumns = useMemo(() => createIotConfigColumns(location.pathname), [location.pathname]);

return (
  <div className="page-product-list">
    <div className="contents-container__table">
      <div className="contents-container__search-wrap">
        <div className="left-area">
          <section>
            <div className="left-area__index">
              <span>조건검색</span>
            </div>
            <div className="left-area__contents">
              <BaseInputWithSearch
                selectValue={params?.searchType}
                inputValue={params?.searchKeyword}
                stateOptions={SEARCH_OPTIONS}
                setStateValue={(searchType: string) => {
                  setParams({ ...params, searchType });
                }}
                onChange={(searchKeyword: string) => {
                  setParams({ ...params, searchKeyword });
                }}
                onSearchClick={() => {
                  navigateWithQueryParams({ page: 0 }, true);
                }}
                onKeyUp={() => {
                  navigateWithQueryParams({ page: 0 }, true);
                }}
                onClearClick={() => {
                  setParams({ ...params, searchKeyword: "", searchType: "" });
                  navigateWithQueryParams({ searchKeyword: "", searchType: "", page: 0 });
                }}
              />
            </div>
            <SearchResetButton />
          </section>
        </div>
        <div className="right-area">
          <BaseButton className="ml20" title="+ IoT 탭 설정" onClick={() => navigate(PagePath.iotConfig.setting)} />
        </div>
      </div>
      <BaseTable
        data={iotDeviceList}
        currentSize={Number(queryParams.size) ?? 20}
        sizeOption={(sizeValue) => {
          navigateWithQueryParams({ size: sizeValue, page: 0 });
        }}
        pageIndex={Number(queryParams.page) ?? 0}
        totalElements={pageMeta?.totalElements}
        totalPages={pageMeta?.totalPages || 0}
        goPage={(page: number) => {
          navigateWithQueryParams({ page });
        }}
        orders={params.sort?.orders || []}
        setOrders={(orders?: Array<Order>) => {
          if (orders) {
            setParams((prev) => ({ ...prev, sort: { orders } }));
            navigateWithQueryParams({ sort: { orders } });
          }
        }}
        disabledSortHeaders={disabledSortHeaders}
        columns={iotConfigColumns}
      />
    </div>
  </div>
);
};

export default IotConfigList;
